import { useEffect, useState, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api, { API_BASE } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { StatusSelect, StatusHistory } from "../components/LeadStatusControls";
import {
  Send, UserCog, Bot, Check, CheckCheck, Clock, X, Sparkles, Phone,
  Download, Files, Paperclip, MessagesSquare, ChevronLeft, Menu,
  Archive, ArchiveRestore, MoreVertical, Pencil, Trash2, Reply, Copy,
  Receipt, Tag, CheckCircle2, XCircle, ShieldAlert, MessageSquareText,
  Smile, ZoomIn, ZoomOut, Pin, Search, Languages,
  Snowflake, Flame, Sun, PauseCircle, PlayCircle, Ban, Zap,
} from "lucide-react";

const FOLLOWUP_LABELS = [
  { key: "cold_followup", name: "Cold Follow-up", stage: "cold", icon: Snowflake, cls: "border-sky-300 text-sky-700 bg-sky-50", on: "bg-sky-600 text-white border-sky-600" },
  { key: "warm_followup", name: "Warm Follow-up", stage: "warm", icon: Sun, cls: "border-yellow-300 text-yellow-700 bg-yellow-50", on: "bg-yellow-500 text-white border-yellow-500" },
  { key: "hot_followup", name: "Hot Follow-up", stage: "hot", icon: Flame, cls: "border-red-300 text-red-700 bg-red-50", on: "bg-red-600 text-white border-red-600" },
];

const REACTIONS = ["❤️", "👍", "😂", "😮", "😢", "🙏"];

// Colour-code a thread by its lead rank so admins can scan Hot/Warm/Cold while scrolling.
function rankAccent(cat, active) {
  if (["Hot", "Very Hot"].includes(cat)) return active ? "border-l-4 border-l-red-500" : "border-l-4 border-l-red-500 bg-red-50/50";
  if (cat === "Warm") return active ? "border-l-4 border-l-yellow-400" : "border-l-4 border-l-yellow-400 bg-yellow-50/50";
  return "border-l-4 border-l-transparent";
}
const rankWeight = (c) => (["Hot", "Very Hot"].includes(c.lead_category) ? 3 : c.lead_category === "Warm" ? 2 : 1);

// Full-screen image viewer (dashboard-only). Aspect ratio preserved, zoom, close.
function ImageLightbox({ path, onClose }) {
  const [zoom, setZoom] = useState(1);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div data-testid="image-lightbox" onClick={onClose}
      className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center animate-in fade-in duration-150">
      <button data-testid="lightbox-close" onClick={onClose}
        className="absolute top-4 right-4 z-10 text-white/90 hover:text-white bg-white/10 hover:bg-white/20 rounded-full p-2 transition-colors">
        <X className="h-6 w-6" />
      </button>
      <div className="absolute bottom-5 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2">
        <button data-testid="lightbox-zoom-out" onClick={(e) => { e.stopPropagation(); setZoom((z) => Math.max(1, +(z - 0.5).toFixed(1))); }}
          className="text-white/90 hover:text-white bg-white/10 hover:bg-white/20 rounded-full p-2 transition-colors"><ZoomOut className="h-5 w-5" /></button>
        <span className="text-white/80 text-xs font-mono w-10 text-center">{Math.round(zoom * 100)}%</span>
        <button data-testid="lightbox-zoom-in" onClick={(e) => { e.stopPropagation(); setZoom((z) => Math.min(4, +(z + 0.5).toFixed(1))); }}
          className="text-white/90 hover:text-white bg-white/10 hover:bg-white/20 rounded-full p-2 transition-colors"><ZoomIn className="h-5 w-5" /></button>
      </div>
      <img src={`${API_BASE}/assets/file/${path}`} alt="attachment"
        onClick={(e) => e.stopPropagation()} style={{ transform: `scale(${zoom})` }}
        className="max-h-[88vh] max-w-[92vw] object-contain select-none transition-transform duration-150 cursor-zoom-in" />
    </div>
  );
}

// Small WhatsApp-style reaction pill attached to the bottom edge of a bubble.
function ReactionPills({ reactions, inbound }) {
  const groups = {};
  (reactions || []).forEach((r) => { if (r.emoji) groups[r.emoji] = (groups[r.emoji] || 0) + 1; });
  const entries = Object.entries(groups);
  if (!entries.length) return null;
  return (
    <div data-testid="reaction-pills"
      className={`absolute -bottom-3 ${inbound ? "left-2" : "right-2"} flex items-center gap-1 bg-white border border-[#E9EDEF] rounded-full shadow-sm px-1.5 py-0.5`}>
      {entries.map(([e, c]) => (
        <span key={e} className="flex items-center text-xs leading-none">
          <span>{e}</span>{c > 1 && <span className="ml-0.5 text-[10px] text-[#54656F]">{c}</span>}
        </span>
      ))}
    </div>
  );
}

// Reaction trigger + emoji picker (used on both inbound and outbound bubbles).
function ReactButton({ m, open, onToggle, onPick, mine }) {
  return (
    <div className="relative self-center">
      <button data-testid={`react-btn-${m.message_id}`} onClick={onToggle} title="React"
        className="opacity-60 hover:opacity-100 text-[#8696A0] hover:text-[#111B21] transition-opacity p-1">
        <Smile className="h-4 w-4" />
      </button>
      {open && (
        <div data-testid={`react-picker-${m.message_id}`}
          className="absolute bottom-7 left-1/2 -translate-x-1/2 z-30 flex gap-0.5 bg-white border border-[#E9EDEF] rounded-full shadow-xl px-1.5 py-1">
          {REACTIONS.map((e) => (
            <button key={e} data-testid={`react-emoji-${m.message_id}-${e}`} onClick={() => onPick(e)}
              className={`text-lg leading-none px-0.5 rounded-full transition-transform hover:scale-125 ${mine === e ? "bg-[#DCF8C6]" : "hover:bg-[#F0F2F5]"}`}>{e}</button>
          ))}
        </div>
      )}
    </div>
  );
}

function leadBadge(cat) {
  const map = {
    "Very Hot": "bg-red-100 text-red-700 border-red-200",
    Hot: "bg-red-100 text-red-700 border-red-200",
    Warm: "bg-yellow-100 text-yellow-700 border-yellow-200",
    Cold: "bg-[#F0F2F5] text-[#54656F] border-[#E9EDEF]",
  };
  return map[cat] || map.Cold;
}

function StatusTick({ status }) {
  if (status === "READ") return <CheckCheck className="h-3.5 w-3.5 text-[#53BDEB]" />;
  if (status === "DELIVERED") return <CheckCheck className="h-3.5 w-3.5 text-[#8696A0]" />;
  if (status === "SENT") return <Check className="h-3.5 w-3.5 text-[#8696A0]" />;
  if (status === "FAILED") return <X className="h-3.5 w-3.5 text-red-500" />;
  return <Clock className="h-3 w-3 text-[#8696A0]" />;
}

function StatusLabel({ status, code }) {
  const map = { SENT: "Sent", DELIVERED: "Delivered", READ: "Read", FAILED: "Failed" };
  const label = map[status] || "Pending";
  const failed = status === "FAILED";
  return (
    <span data-testid="msg-delivery-status"
      className={`inline-flex items-center gap-1 font-mono text-[9px] ${failed ? "text-red-500" : "text-[#8696A0]"}`}
      title={failed && code ? `Meta error code ${code}` : label}>
      <StatusTick status={status} />
      {label}{failed && code ? ` · ${code}` : ""}
    </span>
  );
}

function fmtTime(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
}

// A conversation is "NEW" for 24 hours from when it started (first message / created_at).
function isNewConversation(c) {
  const started = c.created_at || c.first_message_at;
  if (!started) return false;
  try { return (Date.now() - new Date(started).getTime()) < 24 * 60 * 60 * 1000; }
  catch { return false; }
}


// Render AI/agent text as clean WhatsApp-style content (no raw markdown chars).
function formatContent(text) {
  if (!text) return null;
  const clean = String(text)
    .replace(/```[a-zA-Z]*\n?/g, "").replace(/```/g, "")
    .replace(/^\s{0,3}#{1,6}\s*/gm, "")
    .replace(/^\s{0,3}>\s?/gm, "");
  const lines = clean.split("\n");
  const renderInline = (s, key) => {
    const parts = [];
    let rest = s, idx = 0;
    const re = /\*\*(.+?)\*\*|__(.+?)__|(?<!\*)\*(?!\*)(.+?)\*(?!\*)|`([^`]+?)`/;
    let m;
    while ((m = rest.match(re))) {
      if (m.index > 0) parts.push(rest.slice(0, m.index));
      const bold = m[1] || m[2];
      const italic = m[3];
      const code = m[4];
      if (bold) parts.push(<strong key={`${key}-${idx}`}>{bold}</strong>);
      else if (italic) parts.push(<em key={`${key}-${idx}`}>{italic}</em>);
      else if (code) parts.push(<span key={`${key}-${idx}`}>{code}</span>);
      rest = rest.slice(m.index + m[0].length);
      idx++;
    }
    if (rest) parts.push(rest);
    return parts;
  };
  return lines.map((ln, i) => (
    <span key={i}>{renderInline(ln, i)}{i < lines.length - 1 ? <br /> : null}</span>
  ));
}

// Internal admin-only block shown under a customer image (GPT vision result).
function VisionBlock({ m, onVerify }) {
  const v = m.vision_analysis;
  if (!v || !v.classification) return null;
  const cls = v.classification;
  const pay = v.payment || {};
  const mo = v.matched_offer || {};
  const status = m.payment_verification;

  if (cls === "payment_proof") {
    return (
      <div className="mt-1.5 rounded-lg border border-amber-300 bg-amber-50 p-2.5" data-testid="vision-payment-block">
        <div className="flex items-center gap-1.5 text-amber-800 font-semibold text-xs">
          <Receipt className="h-3.5 w-3.5" /> Payment Proof Detected
        </div>
        <div className="mt-1.5 grid grid-cols-2 gap-x-3 gap-y-0.5 text-[11px] text-[#54656F]">
          {pay.amount ? <p><span className="text-[#8696A0]">Amount:</span> {pay.amount} {pay.currency}</p> : null}
          {pay.reference_id ? <p className="truncate"><span className="text-[#8696A0]">Ref:</span> {pay.reference_id}</p> : null}
          {pay.method ? <p><span className="text-[#8696A0]">Method:</span> {pay.method}</p> : null}
          {pay.sender ? <p className="truncate"><span className="text-[#8696A0]">From:</span> {pay.sender}</p> : null}
        </div>
        {status === "verified" ? (
          <p className="mt-1.5 flex items-center gap-1 text-[11px] font-semibold text-green-700"><CheckCircle2 className="h-3.5 w-3.5" /> Verified</p>
        ) : status === "rejected" ? (
          <p className="mt-1.5 flex items-center gap-1 text-[11px] font-semibold text-red-600"><XCircle className="h-3.5 w-3.5" /> Rejected</p>
        ) : (
          <>
            <p className="mt-1.5 flex items-center gap-1 text-[10px] text-amber-700"><ShieldAlert className="h-3 w-3" /> Admin verification required</p>
            <div className="mt-1.5 flex gap-1.5">
              <button data-testid={`payment-verify-${m.message_id}`} onClick={() => onVerify(m, "verified")}
                className="flex items-center gap-1 text-[11px] font-semibold bg-[#25D366] text-[#111B21] hover:bg-[#20BD5A] rounded-md px-2 py-1"><CheckCircle2 className="h-3 w-3" /> Verify</button>
              <button data-testid={`payment-reject-${m.message_id}`} onClick={() => onVerify(m, "rejected")}
                className="flex items-center gap-1 text-[11px] font-semibold border border-red-300 text-red-600 hover:bg-red-50 rounded-md px-2 py-1"><XCircle className="h-3 w-3" /> Reject</button>
            </div>
          </>
        )}
      </div>
    );
  }
  if (cls === "offer_image" && (mo.name || v.description)) {
    return (
      <div className="mt-1.5 rounded-lg border border-[#25D366]/40 bg-[#E7FCE8] p-2.5" data-testid="vision-offer-block">
        <div className="flex items-center gap-1.5 text-[#075E54] font-semibold text-xs">
          <Tag className="h-3.5 w-3.5" /> Detected Offer
        </div>
        <p className="mt-1 text-[11px] text-[#111B21]">{mo.name || v.description}
          {mo.confidence ? <span className="ml-1.5 font-mono text-[9px] text-[#8696A0]">({mo.confidence})</span> : null}
        </p>
      </div>
    );
  }
  if ((cls === "general" || cls === "unclear") && v.description) {
    return (
      <div className="mt-1.5 text-[10px] text-[#8696A0] italic" data-testid="vision-general-block">
        AI: {v.description}
      </div>
    );
  }
  return null;
}

export default function Inbox() {
  const { user } = useAuth();
  const myUserId = user?.user_id;
  const [convos, setConvos] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [statusRefresh, setStatusRefresh] = useState(0);
  const [input, setInput] = useState("");
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [showTemplates, setShowTemplates] = useState(false);
  const [savedReplies, setSavedReplies] = useState([]);
  const [showReplies, setShowReplies] = useState(false);
  const [logoPath, setLogoPath] = useState(null);
  const [filter, setFilter] = useState("active");
  const [menuId, setMenuId] = useState(null);
  const [reactPickerId, setReactPickerId] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const [search, setSearch] = useState("");
  const [labelFilter, setLabelFilter] = useState("all");
  const [entryFilter, setEntryFilter] = useState(() => { try { return localStorage.getItem("inbox_entry_filter") || "all"; } catch { return "all"; } });
  const [sortMode, setSortMode] = useState("recent");
  const [availableLabels, setAvailableLabels] = useState([]);
  const [showLabelEditor, setShowLabelEditor] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [labelDraft, setLabelDraft] = useState("");
  const [translations, setTranslations] = useState({});
  const [translatingId, setTranslatingId] = useState(null);
  const [replyEn, setReplyEn] = useState("");
  const [replyOut, setReplyOut] = useState("");
  const [replyLang, setReplyLang] = useState("");
  const [autoInfo, setAutoInfo] = useState(null);      // lead automation detail
  const [showAutoPopup, setShowAutoPopup] = useState(false);
  const [popupEnr, setPopupEnr] = useState(null);      // enrollment shown in popup
  const [confirmStage, setConfirmStage] = useState(null); // {stage,name} pending enroll confirm
  const [conflictEnroll, setConflictEnroll] = useState(null); // {stage,requestedName,existing:[names]}
  const [alreadyInfo, setAlreadyInfo] = useState(null); // {name} already-enrolled notice
  const [replyBusy, setReplyBusy] = useState(false);
  const scrollRef = useRef();
  const mediaRef = useRef();
  const composerRef = useRef();

  useEffect(() => { api.get("/templates").then((r) => setTemplates(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/saved-replies").then((r) => setSavedReplies(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/business-profile").then((r) => setLogoPath(r.data?.logo_path || null)).catch(() => {}); }, []);
  useEffect(() => { api.get("/labels").then((r) => setAvailableLabels(r.data || [])).catch(() => {}); }, []);

  const [searchParams] = useSearchParams();
  useEffect(() => {
    const cid = searchParams.get("conversation");
    if (cid) { setActiveId(cid); setSummary(null); }
  }, [searchParams]);
  useEffect(() => {
    const lang = detail?.conversation?.chat_language;
    if (lang && lang !== "Unknown") setReplyLang((p) => p || lang);
  }, [detail?.conversation?.chat_language]);

  const sendMedia = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !activeId) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("caption", input || "");
    setInput("");
    try {
      const r = await api.post(`/conversations/${activeId}/send-media`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (!r.data.ok) toast.error(r.data.error || "Media send failed");
      else toast.success("Media sent");
      loadDetail(activeId);
    } catch (err) { toast.error(err.response?.data?.detail || "Media send failed"); }
    finally { if (mediaRef.current) mediaRef.current.value = ""; }
  };

  const sendTemplate = async (t) => {
    setShowTemplates(false);
    try {
      const r = await api.post(`/conversations/${activeId}/send-template`,
        { template_name: t.name, language: t.language, body: t.body });
      if (!r.data.ok) toast.error("Template send failed (check approval/connection)");
      else toast.success("Template sent");
      loadDetail(activeId);
    } catch (e) { toast.error(e.response?.data?.detail || "Send failed"); }
  };

  const loadConvos = useCallback(async () => {
    try { const r = await api.get(`/conversations?status=${filter}`); setConvos(r.data); }
    catch (e) {}
  }, [filter]);

  const loadDetail = useCallback(async (id) => {
    if (!id) return;
    try { const r = await api.get(`/conversations/${id}`); setDetail(r.data); }
    catch (e) {}
  }, []);

  useEffect(() => { loadConvos(); const i = setInterval(loadConvos, 8000); return () => clearInterval(i); }, [loadConvos]);
  useEffect(() => {
    if (!activeId) return;
    loadDetail(activeId);
    const i = setInterval(() => loadDetail(activeId), 3000);
    return () => clearInterval(i);
  }, [activeId, loadDetail]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [detail?.messages?.length]);

  const openConvo = (id) => { setActiveId(id); setSummary(null); setTranslations({}); setReplyEn(""); setReplyOut(""); setReplyLang(""); setShowLabelEditor(false); setShowInfo(false); };

  const send = async () => {
    if (!input.trim() || !activeId) return;
    const msg = input.trim(); setInput("");
    if (composerRef.current) composerRef.current.style.height = "auto";
    const post = (allow) => api.post(`/conversations/${activeId}/messages`, { message: msg, ...(allow ? { allow_duplicate: true } : {}) });
    try {
      let r;
      try { r = await post(false); }
      catch (e) {
        // 7 Oct: the same message was already sent to this customer — ask before sending it again
        const d = e.response?.status === 409 ? e.response?.data?.detail : null;
        if (!d || d.code !== "duplicate") throw e;
        const when = d.sent_at ? new Date(d.sent_at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "recently";
        if (!window.confirm(`⚠️ DUPLICATE WARNING\n\nA ${Math.round((d.similarity || 0.8) * 100)}% similar message was already sent to this customer (${when}):\n\n"${String(d.similar_to || "").slice(0, 220)}"\n\nSend it again anyway?`)) { setInput(msg); toast("Not sent — duplicate"); return; }
        r = await post(true);
      }
      if (!r.data.ok) toast.error(r.data.error || "Send failed");
      loadDetail(activeId);
    } catch (e) { const d = e.response?.data?.detail; toast.error((typeof d === "object" ? d?.message : d) || "Send failed"); }
  };

  const takeover = async () => { await api.post(`/conversations/${activeId}/takeover`); loadDetail(activeId); loadConvos(); toast.success("You have taken over. AI paused."); };
  const returnAi = async () => { await api.post(`/conversations/${activeId}/return-to-ai`); loadDetail(activeId); loadConvos(); toast.success("AI resumed."); };

  const genSummary = async () => {
    setSummaryLoading(true);
    try {
      const r = await api.get(`/conversations/${activeId}/summary`);
      setSummary(r.data);
      if (r.data.chat_language && r.data.chat_language !== "Unknown") setReplyLang((p) => p || r.data.chat_language);
    }
    catch (e) { toast.error("Summary failed"); }
    finally { setSummaryLoading(false); }
  };

  const archive = async () => {
    await api.post(`/conversations/${activeId}/archive`);
    toast.success("Chat archived");
    setActiveId(null); setDetail(null); loadConvos();
  };
  const unarchive = async () => {
    await api.post(`/conversations/${activeId}/unarchive`);
    toast.success("Chat moved to Active");
    setActiveId(null); setDetail(null); loadConvos();
  };
  const safeCopy = async (text) => {
    const t = text || "";
    try {
      await navigator.clipboard.writeText(t);
      toast.success("Copied");
    } catch {
      try {
        const ta = document.createElement("textarea");
        ta.value = t; ta.style.position = "fixed"; ta.style.opacity = "0";
        document.body.appendChild(ta); ta.focus(); ta.select();
        document.execCommand("copy"); document.body.removeChild(ta);
        toast.success("Copied");
      } catch { toast.error("Couldn't copy — please select and copy manually"); }
    }
  };
  const copyText = (t) => { safeCopy(t); setMenuId(null); };
  const startEdit = (m) => { setEditingId(m.message_id); setEditText(m.content || ""); setMenuId(null); };
  const saveEdit = async (m) => {
    try {
      const r = await api.put(`/conversations/${activeId}/messages/${m.message_id}`, { message: editText });
      setEditingId(null);
      if (r.data?.note) toast.message("Message updated locally", { description: r.data.note });
      loadDetail(activeId);
    } catch (e) { toast.error(e.response?.data?.detail || "Edit failed"); }
  };
  const delMsg = async (m) => {
    setMenuId(null);
    if (!window.confirm("Delete this message for everyone?")) return;
    try {
      const r = await api.delete(`/conversations/${activeId}/messages/${m.message_id}`);
      if (r.data?.note) toast.message("Message deleted", { description: r.data.note });
      loadDetail(activeId);
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };
  const replyTo = (m) => { setInput(`"${(m.content || "").slice(0, 80)}"\n`); setMenuId(null); };

  const insertReply = (r) => {
    setShowReplies(false);
    setInput((prev) => (prev ? prev + (prev.endsWith("\n") ? "" : "\n") : "") + r.content);
    setTimeout(() => composerRef.current?.focus(), 0);
  };

  const verifyPayment = async (m, status) => {
    try {
      await api.post(`/conversations/${activeId}/messages/${m.message_id}/payment-verify`, { status });
      toast.success(status === "verified" ? "Payment marked verified" : "Payment marked rejected");
      loadDetail(activeId);
    } catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
  };

  const myReaction = (m) => (m.reactions || []).find((r) => r.actor === "admin" && r.user_id === myUserId)?.emoji;

  const reactTo = async (m, emoji) => {
    setReactPickerId(null);
    const mine = myReaction(m);
    const next = mine === emoji ? "" : emoji; // tapping your current reaction removes it
    // optimistic update so it feels instant
    setDetail((d) => {
      if (!d) return d;
      return {
        ...d,
        messages: d.messages.map((x) => {
          if (x.message_id !== m.message_id) return x;
          const rest = (x.reactions || []).filter((r) => !(r.actor === "admin" && r.user_id === myUserId));
          return { ...x, reactions: next ? [...rest, { actor: "admin", user_id: myUserId, emoji: next }] : rest };
        }),
      };
    });
    try {
      await api.post(`/conversations/${activeId}/messages/${m.message_id}/reaction`, { emoji: next });
      loadDetail(activeId);
    } catch (e) { toast.error(e.response?.data?.detail || "Reaction failed"); loadDetail(activeId); }
  };

  const setRank = async (category, source = "manual") => {
    if (!activeId) return;
    try {
      await api.post(`/conversations/${activeId}/lead-rank`, source === "auto" ? { source: "auto" } : { category, source: "manual" });
      toast.success(source === "auto" ? "Rank set to Auto (AI)" : `Lead marked ${category}`);
      loadDetail(activeId); loadConvos();
    } catch { toast.error("Failed to set rank"); }
  };

  const loadAuto = useCallback(async (leadId) => {
    if (!leadId) { setAutoInfo(null); return; }
    try { const r = await api.get(`/automation/lead/${leadId}/detail`); setAutoInfo(r.data); }
    catch { setAutoInfo(null); }
  }, []);
  useEffect(() => { loadAuto(detail?.lead?.lead_id); }, [detail?.lead?.lead_id, loadAuto]);

  const chooseFollowLabel = async (item) => {
    if (!activeId) return;
    const current = detail?.lead?.follow_up_label;
    const next = current === item.key ? null : item.key;
    try {
      const r = await api.post(`/conversations/${activeId}/follow-up-label`, { label: next });
      setDetail((d) => (d ? { ...d, lead: { ...(d.lead || {}), follow_up_label: r.data.follow_up_label } } : d));
      if (next) {
        setConfirmStage({ stage: item.stage, name: item.name });
      } else {
        toast.success("Follow-up label removed");
      }
    } catch { toast.error("Could not set label"); }
  };

  const doEnroll = async (stage, force) => {
    if (!detail?.lead?.lead_id) return;
    try {
      const r = await api.post(`/automation/enroll`, { lead_id: detail.lead.lead_id, stage, force });
      if (r.data.same_automation) {
        setAlreadyInfo({ name: r.data.automation_name });
      } else if (r.data.needs_confirmation) {
        setConflictEnroll({
          stage: r.data.requested_stage,
          requestedName: r.data.requested_automation,
          existing: (r.data.existing_automations || []).map((e) => e.name),
        });
      } else {
        toast.success(`Added to ${r.data.automation_name || "automation"}`);
      }
      loadAuto(detail.lead.lead_id);
    } catch { toast.error("Could not enroll"); }
  };

  const confirmEnroll = async (doIt) => {
    const stage = confirmStage?.stage;
    setConfirmStage(null);
    if (!doIt || !stage) return;
    await doEnroll(stage, false);
  };

  const autoAction = async (action, enrollmentId) => {
    const leadId = detail?.lead?.lead_id;
    if (!leadId) return;
    try {
      await api.post(`/automation/lead/${leadId}/${action}`, enrollmentId ? { enrollment_id: enrollmentId } : {});
      toast.success(`Automation ${action === "remove" ? "removed" : action + "d"}`);
      loadAuto(leadId);
      if (action === "remove") setShowAutoPopup(false);
    } catch { toast.error("Action failed"); }
  };

  const conv = detail?.conversation;
  const contact = detail?.contact;
  const lead = detail?.lead;

  const togglePin = async () => {
    try {
      const r = await api.post(`/conversations/${activeId}/pin`, { pinned: !conv?.pinned });
      setDetail((d) => (d ? { ...d, conversation: { ...d.conversation, pinned: r.data.pinned } } : d));
      toast.success(r.data.pinned ? "Chat pinned" : "Chat unpinned");
      loadConvos();
    } catch { toast.error("Pin failed"); }
  };
  const saveLabels = async (labels) => {
    try {
      const r = await api.post(`/conversations/${activeId}/labels`, { labels });
      setDetail((d) => (d ? { ...d, conversation: { ...d.conversation, labels: r.data.labels } } : d));
      setAvailableLabels((prev) => Array.from(new Set([...prev, ...r.data.labels])));
      loadConvos();
    } catch { toast.error("Could not update labels"); }
  };
  const toggleLabel = (label) => {
    const cur = conv?.labels || [];
    saveLabels(cur.includes(label) ? cur.filter((l) => l !== label) : [...cur, label]);
  };
  const addCustomLabel = () => {
    const l = labelDraft.trim();
    if (!l) return;
    const cur = conv?.labels || [];
    if (!cur.includes(l)) saveLabels([...cur, l]);
    setLabelDraft("");
  };
  const translateMessage = async (m) => {
    setMenuId(null);
    setTranslatingId(m.message_id);
    try {
      const r = await api.post(`/conversations/${activeId}/translate-message`, { message_id: m.message_id });
      setTranslations((t) => ({ ...t, [m.message_id]: r.data }));
      if (r.data.source_language && r.data.source_language !== "Unknown") setReplyLang((p) => p || r.data.source_language);
    } catch { toast.error("Translation failed"); }
    finally { setTranslatingId(null); }
  };
  const doReplyTranslate = async () => {
    if (!replyEn.trim()) return;
    setReplyBusy(true);
    try {
      const r = await api.post(`/translate`, { text: replyEn, target_language: replyLang || "Hindi" });
      setReplyOut(r.data.translated || "");
    } catch { toast.error("Translation failed"); }
    finally { setReplyBusy(false); }
  };

  // 19 Oct 2026 (RK): filter by what the chat came in for (the ready text of an ad / website / channel page)
  const entryCounts = convos.reduce((m, c) => {
    const k = c.entry_key || "none";
    if (!m[k]) m[k] = { label: c.entry_label || "No customer message yet", n: 0 };
    m[k].n += 1;
    return m;
  }, {});
  const entryOptions = Object.entries(entryCounts).sort((a, b) => b[1].n - a[1].n);
  const chooseEntry = (v) => { setEntryFilter(v); try { localStorage.setItem("inbox_entry_filter", v); } catch { /* ignore */ } };

  const visibleConvos = convos.filter((c) => {
    if (entryFilter !== "all" && (c.entry_key || "none") !== entryFilter) return false;
    if (labelFilter === "__new24h__") {
      if (!isNewConversation(c)) return false;
    } else if (labelFilter !== "all" && !(c.labels || []).includes(labelFilter)) return false;
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (c.contact_name || "").toLowerCase().includes(q) ||
      (c.contact_phone || "").toLowerCase().includes(q) ||
      (c.last_message || "").toLowerCase().includes(q);
  }).sort((a, b) => {
    if (!!b.pinned !== !!a.pinned) return (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0);
    if (sortMode === "rank") {
      const d = rankWeight(b) - rankWeight(a);
      if (d) return d;
    }
    if (sortMode === "new") {
      const d = (isNewConversation(b) ? 1 : 0) - (isNewConversation(a) ? 1 : 0);
      if (d) return d;
    }
    return new Date(b.last_message_at || 0) - new Date(a.last_message_at || 0);
  });

  return (
    <div className="flex-1 min-h-0 flex overflow-hidden bg-[#F0F2F5]">
      {/* Left: list */}
      <div className={`${activeId ? "hidden md:flex" : "flex"} w-full md:w-80 flex-shrink-0 border-r border-[#E9EDEF] bg-white flex-col`}>
        <div className="h-16 flex items-center px-4 md:px-5 bg-[#075E54]">
          <button className="md:hidden mr-3 -ml-1 text-white/90 hover:text-white" data-testid="inbox-menu"
            onClick={() => window.dispatchEvent(new Event("open-sidebar"))}>
            <Menu className="h-6 w-6" />
          </button>
          <MessagesSquare className="h-4 w-4 text-white/80 mr-2" />
          <h2 className="font-heading text-sm font-bold text-white">Conversations</h2>
          <span className="ml-auto font-mono text-[10px] text-white/70">{visibleConvos.length}</span>
        </div>
        <div className="flex border-b border-[#E9EDEF] flex-shrink-0">
          {[["active", "Active"], ["archived", "Archived"]].map(([v, label]) => (
            <button key={v} data-testid={`inbox-filter-${v}`} onClick={() => { setFilter(v); setActiveId(null); setDetail(null); }}
              className={`flex-1 text-xs py-2.5 font-medium transition-colors ${filter === v ? "text-[#075E54] border-b-2 border-[#25D366] bg-[#E7FCE8]/40" : "text-[#8696A0] hover:text-[#54656F]"}`}>
              {label}
            </button>
          ))}
        </div>
        <div className="px-3 py-2 border-b border-[#E9EDEF] space-y-2 flex-shrink-0">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#8696A0]" />
            <input data-testid="inbox-search" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, phone or message"
              className="w-full pl-8 pr-3 py-1.5 text-xs bg-[#F0F2F5] border border-transparent focus:border-[#25D366] rounded-lg focus:outline-none text-[#111B21]" />
          </div>
          <select data-testid="inbox-entry-filter" value={entryFilter} onChange={(e) => chooseEntry(e.target.value)}
            title="What the customer first wrote (ad / website / channel page message)"
            className={`w-full py-1.5 px-2 text-xs border rounded-lg focus:outline-none ${entryFilter !== "all" ? "bg-[#E7FCE8] border-[#25D366] text-[#075E54] font-semibold" : "bg-[#F0F2F5] border-transparent focus:border-[#25D366] text-[#54656F]"}`}>
            <option value="all">All enquiries ({convos.length})</option>
            {entryOptions.map(([k, v]) => <option key={k} value={k}>{v.label} ({v.n})</option>)}
          </select>
          <div className="flex gap-2">
            <select data-testid="inbox-label-filter" value={labelFilter} onChange={(e) => setLabelFilter(e.target.value)}
              className="flex-1 min-w-0 py-1.5 px-2 text-xs bg-[#F0F2F5] border border-transparent focus:border-[#25D366] rounded-lg focus:outline-none text-[#54656F]">
              <option value="all">All labels</option>
              <option value="__new24h__">🟢 New (last 24h)</option>
              {availableLabels.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
            <select data-testid="inbox-sort" value={sortMode} onChange={(e) => setSortMode(e.target.value)}
              className="flex-1 min-w-0 py-1.5 px-2 text-xs bg-[#F0F2F5] border border-transparent focus:border-[#25D366] rounded-lg focus:outline-none text-[#54656F]">
              <option value="recent">Sort: Recent</option>
              <option value="new">Sort: New first</option>
              <option value="rank">Sort: Lead rank 🔥</option>
            </select>
          </div>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto" data-testid="conversation-list">
          {visibleConvos.length === 0 && (
            <p className="px-5 py-10 text-center text-xs text-[#8696A0]">{(search || labelFilter !== "all" || entryFilter !== "all") ? "No chats match your search / filters." : (filter === "archived" ? "No archived chats." : "No conversations yet.")}<br />{(search || labelFilter !== "all" || entryFilter !== "all") ? "Try clearing the filter." : (filter === "archived" ? "Archived chats move here." : "They appear when a customer messages your WhatsApp number.")}</p>
          )}
          {visibleConvos.map((c) => (
            <button key={c.conversation_id} data-testid="inbox-thread-item" onClick={() => openConvo(c.conversation_id)}
              className={`w-full text-left px-4 py-3 border-b border-[#F0F2F5] transition-colors ${rankAccent(c.lead_category, activeId === c.conversation_id)} ${activeId === c.conversation_id ? "bg-[#E7FCE8]" : "hover:bg-[#F0F2F5]"}`}>
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-[#25D366] flex items-center justify-center text-sm font-semibold text-white flex-shrink-0">
                  {(c.contact_name || "?")[0]?.toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    {c.pinned && <Pin className="h-3 w-3 text-[#075E54] fill-[#075E54] flex-shrink-0" data-testid="thread-pin-indicator" />}
                    <span className="text-sm font-semibold text-[#111B21] truncate flex-1">{c.contact_name}</span>
                    {isNewConversation(c) && (
                      <span data-testid="thread-new-badge" title="Started in the last 24 hours"
                        className="text-[8px] font-bold px-1.5 py-0.5 rounded bg-[#25D366] text-white flex-shrink-0 tracking-wide">NEW</span>
                    )}
                    {["Hot", "Very Hot"].includes(c.lead_category) && <span data-testid="thread-fire" title="Hot lead" className="text-sm flex-shrink-0 leading-none">🔥</span>}
                    <span className="font-mono text-[9px] text-[#8696A0]">{fmtTime(c.last_message_at)}</span>
                  </div>
                  <p className="text-xs text-[#54656F] truncate mt-0.5">{c.last_message || "—"}</p>
                  {c.entry_key && !["other", "none"].includes(c.entry_key) && (
                    <p data-testid="thread-entry" title={c.entry_text || ""} className="text-[9px] text-[#8696A0] truncate mt-0.5">📣 {c.entry_label}</p>
                  )}
                  <div className="flex items-center gap-1.5 mt-1.5">
                    <span className={`font-mono text-[9px] px-1.5 py-0.5 rounded-md border ${c.ai_mode === "AI_ACTIVE" ? "bg-[#DCF8C6] text-[#075E54] border-[#25D366]/40" : "bg-[#E0F2FE] text-[#0284C7] border-[#7DD3FC]"}`}>
                      {c.ai_mode === "AI_ACTIVE" ? "AI" : "HUMAN"}
                    </span>
                    {c.lead_score != null && (
                      <span className={`font-mono text-[9px] px-1.5 py-0.5 rounded-md border ${leadBadge(c.lead_category)}`}>{c.lead_category || c.lead_score}</span>
                    )}
                    {c.unread > 0 && <span className="ml-auto h-5 min-w-5 px-1 rounded-full bg-[#25D366] text-white text-[10px] font-semibold flex items-center justify-center">{c.unread}</span>}
                  </div>
                  {(c.labels || []).length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1.5" data-testid="thread-labels">
                      {c.labels.slice(0, 3).map((l) => (
                        <span key={l} className="text-[8px] font-medium px-1.5 py-0.5 rounded bg-[#E7FCE8] text-[#075E54] border border-[#25D366]/30">{l}</span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Center: conversation */}
      <div className={`${activeId ? "flex" : "hidden md:flex"} flex-1 flex-col wa-chat-bg min-w-0 min-h-0`}>
        {!conv ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center px-8">
            <div className="h-20 w-20 rounded-full bg-[#E7FCE8] flex items-center justify-center mb-4">
              <MessagesSquare className="h-9 w-9 text-[#075E54]" />
            </div>
            <p className="text-sm text-[#54656F] max-w-xs">Select a conversation to view messages. Chats appear here when a customer messages your WhatsApp number.</p>
          </div>
        ) : (
          <>
            <div className="h-16 flex-shrink-0 flex items-center justify-between gap-2 px-3 md:px-6 bg-[#075E54]">
              <div className="flex items-center gap-2 md:gap-3 min-w-0">
                <button data-testid="chat-back" onClick={() => { setActiveId(null); setDetail(null); }}
                  className="md:hidden -ml-1 text-white/80 hover:text-white flex-shrink-0">
                  <ChevronLeft className="h-6 w-6" />
                </button>
                <button data-testid="chat-customer-info-trigger" onClick={() => setShowInfo(true)}
                  className="flex items-center gap-2 md:gap-3 min-w-0 text-left" title="View customer info & summary">
                  <div className="h-9 w-9 md:h-10 md:w-10 rounded-full bg-white/15 flex items-center justify-center text-sm font-semibold text-white flex-shrink-0">
                    {contact?.name?.[0]?.toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-white truncate">{contact?.name}</p>
                    <p className="font-mono text-[10px] text-white/70 truncate">{contact?.phone}</p>
                  </div>
                </button>
              </div>
              <div className="flex items-center gap-1.5 md:gap-2 flex-shrink-0">
                <span className={`font-mono text-[10px] px-2 py-1 rounded-md ${conv.ai_mode === "AI_ACTIVE" ? "bg-[#DCF8C6] text-[#075E54]" : "bg-white/15 text-white"}`}>
                  {conv.ai_mode === "AI_ACTIVE" ? "AI" : "HUMAN"}<span className="hidden md:inline">{conv.ai_mode === "AI_ACTIVE" ? " ACTIVE" : " ACTIVE"}</span>
                </span>
                {conv.ai_mode === "AI_ACTIVE" ? (
                  <button data-testid="takeover-button" onClick={takeover}
                    className="flex items-center gap-1.5 bg-white/10 border border-white/20 text-white hover:bg-white/20 rounded-xl px-2.5 md:px-3 py-1.5 text-xs font-medium transition-colors">
                    <UserCog className="h-3.5 w-3.5" /> <span className="hidden sm:inline">Take Over</span>
                  </button>
                ) : (
                  <button data-testid="return-ai-button" onClick={returnAi}
                    className="flex items-center gap-1.5 bg-[#25D366] text-[#111B21] hover:bg-[#20BD5A] rounded-xl px-2.5 md:px-3 py-1.5 text-xs font-semibold transition-colors">
                    <Bot className="h-3.5 w-3.5" /> <span className="hidden sm:inline">Return to AI</span>
                  </button>
                )}
                <button data-testid="pin-button" onClick={togglePin} title={conv.pinned ? "Unpin chat" : "Pin chat"}
                  className={`flex items-center justify-center h-8 w-8 border rounded-xl transition-colors ${conv.pinned ? "bg-white text-[#075E54] border-white" : "bg-white/10 border-white/20 text-white hover:bg-white/20"}`}>
                  <Pin className={`h-3.5 w-3.5 ${conv.pinned ? "fill-[#075E54]" : ""}`} />
                </button>
                <div className="relative">
                  <button data-testid="labels-button" onClick={() => setShowLabelEditor((s) => !s)} title="Labels"
                    className={`flex items-center justify-center h-8 w-8 border rounded-xl transition-colors ${(conv.labels || []).length ? "bg-white text-[#075E54] border-white" : "bg-white/10 border-white/20 text-white hover:bg-white/20"}`}>
                    <Tag className="h-3.5 w-3.5" />
                  </button>
                  {showLabelEditor && (
                    <div className="absolute right-0 top-10 z-40 w-56 bg-white border border-[#E9EDEF] rounded-xl shadow-2xl p-3" data-testid="label-editor">
                      <p className="font-mono text-[9px] uppercase tracking-widest text-[#8696A0] mb-2">Labels</p>
                      <div className="space-y-1 max-h-48 overflow-y-auto">
                        {availableLabels.map((l) => {
                          const on = (conv.labels || []).includes(l);
                          return (
                            <button key={l} data-testid={`label-toggle-${l}`} onClick={() => toggleLabel(l)}
                              className={`w-full flex items-center justify-between text-left text-xs px-2 py-1.5 rounded-lg transition-colors ${on ? "bg-[#E7FCE8] text-[#075E54] font-medium" : "hover:bg-[#F0F2F5] text-[#374151]"}`}>
                              <span>{l}</span>{on && <Check className="h-3 w-3" />}
                            </button>
                          );
                        })}
                      </div>
                      <div className="flex gap-1 mt-2 pt-2 border-t border-[#E9EDEF]">
                        <input data-testid="label-custom-input" value={labelDraft} onChange={(e) => setLabelDraft(e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustomLabel(); } }} placeholder="Custom label"
                          className="flex-1 min-w-0 text-xs px-2 py-1 border border-[#CBD5E1] rounded-lg focus:outline-none focus:border-[#25D366] text-[#111B21]" />
                        <button data-testid="label-custom-add" onClick={addCustomLabel} className="text-xs px-2 py-1 rounded-lg bg-[#25D366] text-[#111B21] font-semibold">Add</button>
                      </div>
                    </div>
                  )}
                </div>
                {conv.archived ? (
                  <button data-testid="unarchive-button" onClick={unarchive} title="Move to Active"
                    className="flex items-center justify-center h-8 w-8 bg-white/10 border border-white/20 text-white hover:bg-white/20 rounded-xl transition-colors">
                    <ArchiveRestore className="h-3.5 w-3.5" />
                  </button>
                ) : (
                  <button data-testid="archive-button" onClick={archive} title="Archive chat"
                    className="flex items-center justify-center h-8 w-8 bg-white/10 border border-white/20 text-white hover:bg-white/20 rounded-xl transition-colors">
                    <Archive className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </div>

            <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto p-6 space-y-2" data-testid="message-thread">
              {(detail?.messages || []).map((m, idx) => {
                const menuDown = idx < 2;
                const inbound = m.direction === "INBOUND";
                if (m.message_type === "call") {
                  // 7 Oct: phone calls with Vivaan are logged in the caller's chat (internal, never sent to WhatsApp)
                  const [head, ...rest] = String(m.content || "").split("\n");
                  return (
                    <div key={m.message_id} className="flex justify-center" data-testid={`call-card-${m.message_id}`}>
                      <div className="max-w-[80%] w-[420px] rounded-lg border border-[#B9E1F5] bg-[#EAF6FC] px-3 py-2 shadow-sm">
                        <p className="text-xs font-semibold text-[#075E54] flex items-center gap-1.5">
                          <Phone className="h-3.5 w-3.5" /> {head}
                          <span className="ml-auto font-normal text-[10px] text-[#8696A0]">{fmtTime(m.created_at)}</span>
                        </p>
                        {rest.length > 0 && <p className="mt-1 text-[13px] whitespace-pre-wrap break-words text-[#111B21]">{rest.join("\n")}</p>}
                        {m.has_recording && m.call_id && (
                          <audio controls preload="none" src={`${API_BASE}/voice/calls/${m.call_id}/recording`} className="mt-1.5 w-full h-9" />
                        )}
                        <p className="mt-1 text-[10px] text-[#8696A0]">Internal call log, not sent to the customer</p>
                      </div>
                    </div>
                  );
                }
                if (m.deleted) {
                  return (
                    <div key={m.message_id} className={`flex ${inbound ? "justify-start" : "justify-end"}`} data-testid="deleted-message">
                      <div className={`max-w-[72%] py-1.5 px-2.5 rounded-lg border border-dashed border-[#CBD5E1] ${inbound ? "bg-white" : "bg-[#EFF3EE]"}`}>
                        <p className="text-sm italic text-[#8696A0]">This message was deleted</p>
                      </div>
                    </div>
                  );
                }
                const editing = editingId === m.message_id;
                return (
                  <div key={m.message_id} className={`group flex items-end gap-1 ${inbound ? "justify-start" : "justify-end"} ${(m.reactions || []).some((r) => r.emoji) ? "mb-3" : ""}`}>
                    {!inbound && !editing && (
                      <div className="flex items-center gap-0.5 self-center">
                        <ReactButton m={m} open={reactPickerId === m.message_id}
                          onToggle={() => { setReactPickerId(reactPickerId === m.message_id ? null : m.message_id); setMenuId(null); }}
                          onPick={(e) => reactTo(m, e)} mine={myReaction(m)} />
                        <div className="relative">
                        <button data-testid={`msg-menu-${m.message_id}`} onClick={() => setMenuId(menuId === m.message_id ? null : m.message_id)}
                          className="opacity-60 hover:opacity-100 text-[#8696A0] hover:text-[#111B21] transition-opacity p-1">
                          <MoreVertical className="h-4 w-4" />
                        </button>
                        {menuId === m.message_id && (
                          <div className={`absolute right-0 ${menuDown ? "top-7" : "bottom-6"} z-40 w-36 bg-white border border-[#E9EDEF] rounded-lg shadow-xl py-1 text-sm`} data-testid="msg-menu-popover">
                            {m.message_type === "text" && (
                              <button onClick={() => startEdit(m)} className="w-full flex items-center gap-2 px-3 py-1.5 hover:bg-[#F0F2F5] text-[#111B21]"><Pencil className="h-3.5 w-3.5" /> Edit</button>
                            )}
                            <button onClick={() => copyText(m.content)} className="w-full flex items-center gap-2 px-3 py-1.5 hover:bg-[#F0F2F5] text-[#111B21]"><Copy className="h-3.5 w-3.5" /> Copy</button>
                            <button data-testid={`msg-delete-${m.message_id}`} onClick={() => delMsg(m)} className="w-full flex items-center gap-2 px-3 py-1.5 hover:bg-red-50 text-red-600"><Trash2 className="h-3.5 w-3.5" /> Unsend</button>
                          </div>
                        )}
                        </div>
                      </div>
                    )}
                    <div className="relative max-w-[72%]">
                    <div className={`py-1.5 px-2.5 rounded-lg shadow-sm ${inbound ? "bg-white text-[#111B21] rounded-tl-sm" : "bg-[#D9FDD3] text-[#111B21] rounded-tr-sm"}`}>
                      {!inbound && (
                        <p className="text-[10px] font-semibold mb-1 text-[#075E54] flex items-center gap-1.5">
                          {m.sender_type === "AI" ? (
                            logoPath ? <img src={`${API_BASE}/assets/file/${logoPath}`} alt="" className="h-4 w-4 rounded-full object-cover" /> : <Bot className="h-3 w-3" />
                          ) : <UserCog className="h-3 w-3" />}
                          {m.sender_type === "AI" ? "AI" : "Agent"}
                        </p>
                      )}
                      {m.media_path && (m.message_type === "image" ? (
                        <img src={`${API_BASE}/assets/file/${m.media_path}`} alt="attachment"
                          data-testid={`chat-image-${m.message_id}`} onClick={() => setLightbox(m.media_path)}
                          className="rounded-lg mb-1 max-h-56 w-auto object-cover cursor-pointer hover:brightness-95 transition" />
                      ) : m.message_type === "audio" ? (
                        <div className="mb-1 w-[280px] max-w-[70vw]" data-testid={`chat-audio-${m.message_id}`}>
                          {/* 7 Oct (RK: "not able to listen to the shared audio"): the player collapsed to ~70px inside the
                              shrink-to-fit bubble, so only the ⋮ menu showed. It now has its own width + a direct link. */}
                          <audio controls preload="metadata" src={`${API_BASE}/assets/file/${m.media_path}`} className="w-full min-w-[240px] h-10" />
                          <a href={`${API_BASE}/assets/file/${m.media_path}`} target="_blank" rel="noreferrer" className="text-[11px] text-[#027EB5] hover:underline">Open / download audio</a>
                          {m.transcription_status === "transcribing" && (
                            <p className="text-[11px] text-[#8696A0] italic mt-1 flex items-center gap-1">
                              <span className="wa-typing scale-75 origin-left"><i></i><i></i><i></i></span> Transcribing
                            </p>
                          )}
                          {m.transcription_status === "transcribed" && m.transcription && (
                            <div className="mt-1.5 border-l-2 border-[#25D366] pl-2" data-testid="voice-transcription">
                              <p className="text-[9px] font-mono uppercase tracking-wider text-[#8696A0]">Transcription</p>
                              <p className="text-sm whitespace-pre-wrap break-words text-[#111B21]">{m.transcription}</p>
                            </div>
                          )}
                          {m.transcription_status === "failed" && (
                            <p className="text-[11px] text-amber-600 italic mt-1">Transcription unavailable</p>
                          )}
                        </div>
                      ) : (
                        <a href={`${API_BASE}/assets/file/${m.media_path}`} target="_blank" rel="noreferrer" download
                          className="flex items-center gap-2 mb-1 text-xs text-[#075E54] underline decoration-dotted">
                          <Download className="h-3.5 w-3.5" /> Download {m.message_type}
                        </a>
                      ))}
                      {inbound && m.message_type === "image" && <VisionBlock m={m} onVerify={verifyPayment} />}
                      {editing ? (
                        <div className="space-y-1.5">
                          <textarea data-testid="edit-message-input" value={editText} onChange={(e) => setEditText(e.target.value)} rows={2}
                            className="w-full text-sm bg-white border border-[#CBD5E1] rounded-md px-2 py-1 focus:outline-none focus:border-[#25D366]" />
                          <div className="flex gap-1.5 justify-end">
                            <button onClick={() => setEditingId(null)} className="text-xs px-2 py-1 rounded-md text-[#54656F] hover:bg-[#E9EDEF]">Cancel</button>
                            <button data-testid="edit-message-save" onClick={() => saveEdit(m)} className="text-xs px-2.5 py-1 rounded-md bg-[#25D366] text-[#111B21] font-semibold">Save</button>
                          </div>
                        </div>
                      ) : (
                        m.message_type !== "audio" && <p className="text-sm whitespace-pre-wrap break-words">{formatContent(m.content)}</p>
                      )}
                      {translations[m.message_id] && (
                        <div className="mt-1.5 border-l-2 border-[#25D366] pl-2" data-testid={`translation-${m.message_id}`}>
                          <p className="text-[9px] font-mono uppercase tracking-wider text-[#8696A0]">Translation{translations[m.message_id].source_language && translations[m.message_id].source_language !== "Unknown" ? ` · from ${translations[m.message_id].source_language}` : ""}</p>
                          <p className="text-sm whitespace-pre-wrap break-words text-[#111B21]">{translations[m.message_id].translated}</p>
                        </div>
                      )}
                      {translatingId === m.message_id && <p className="text-[11px] text-[#8696A0] italic mt-1">Translating…</p>}
                      <div className="flex items-center gap-1 mt-0.5 justify-end">
                        {m.edited && <span className="text-[9px] text-[#8696A0] italic mr-0.5">edited</span>}
                        <span className="font-mono text-[9px] text-[#8696A0]">{fmtTime(m.created_at)}</span>
                        {!inbound && <StatusLabel status={m.status} code={m.failure_code ?? m.error_code} />}
                      </div>
                      {!inbound && m.status === "FAILED" && (m.failure_reason || m.error) && (
                        <div className="mt-1 flex items-start gap-1 rounded-md bg-red-50 border border-red-200 px-2 py-1" data-testid={`msg-failure-${m.message_id}`}>
                          <XCircle className="h-3 w-3 text-red-500 mt-0.5 shrink-0" />
                          <p className="text-[10px] text-red-600 leading-snug">Not delivered — {m.failure_reason || m.error}</p>
                        </div>
                      )}
                    </div>
                    <ReactionPills reactions={m.reactions} inbound={inbound} />
                    </div>
                    {inbound && (
                      <div className="flex items-center gap-0.5 self-center">
                        <div className="relative">
                        <button data-testid={`msg-menu-${m.message_id}`} onClick={() => setMenuId(menuId === m.message_id ? null : m.message_id)}
                          className="opacity-60 hover:opacity-100 text-[#8696A0] hover:text-[#111B21] transition-opacity p-1">
                          <MoreVertical className="h-4 w-4" />
                        </button>
                        {menuId === m.message_id && (
                          <div className={`absolute left-0 ${menuDown ? "top-7" : "bottom-6"} z-40 w-36 bg-white border border-[#E9EDEF] rounded-lg shadow-xl py-1 text-sm`}>
                            <button onClick={() => copyText(m.content)} className="w-full flex items-center gap-2 px-3 py-1.5 hover:bg-[#F0F2F5] text-[#111B21]"><Copy className="h-3.5 w-3.5" /> Copy</button>
                            <button data-testid={`translate-msg-${m.message_id}`} onClick={() => translateMessage(m)} className="w-full flex items-center gap-2 px-3 py-1.5 hover:bg-[#F0F2F5] text-[#111B21]"><Languages className="h-3.5 w-3.5" /> Translate</button>
                            <button onClick={() => replyTo(m)} className="w-full flex items-center gap-2 px-3 py-1.5 hover:bg-[#F0F2F5] text-[#111B21]"><Reply className="h-3.5 w-3.5" /> Reply</button>
                          </div>
                        )}
                        </div>
                        <ReactButton m={m} open={reactPickerId === m.message_id}
                          onToggle={() => { setReactPickerId(reactPickerId === m.message_id ? null : m.message_id); setMenuId(null); }}
                          onPick={(e) => reactTo(m, e)} mine={myReaction(m)} />
                      </div>
                    )}
                  </div>
                );
              })}
              {conv?.ai_mode === "AI_ACTIVE" && conv?.ai_typing &&
                conv?.ai_typing_at && (Date.now() - new Date(conv.ai_typing_at).getTime() < 90000) && (
                <div className="flex justify-end" data-testid="ai-typing-indicator">
                  <div className="max-w-[72%] py-2 px-3 rounded-lg rounded-tr-sm bg-[#D9FDD3] shadow-sm">
                    <p className="text-[10px] font-semibold mb-1 text-[#075E54] flex items-center gap-1.5">
                      {logoPath ? <img src={`${API_BASE}/assets/file/${logoPath}`} alt="" className="h-4 w-4 rounded-full object-cover" /> : <Bot className="h-3 w-3" />}
                      AI is typing
                    </p>
                    <span className="wa-typing"><i></i><i></i><i></i></span>
                  </div>
                </div>
              )}
            </div>

            <div className="p-3 bg-[#F0F2F5] border-t border-[#E9EDEF] flex items-end gap-2 relative">
              {showReplies && (
                <div className="absolute bottom-16 left-4 right-4 max-h-72 overflow-y-auto border border-[#E9EDEF] bg-white rounded-xl shadow-2xl z-30" data-testid="saved-replies-popover">
                  <div className="px-4 py-2.5 border-b border-[#E9EDEF] flex items-center justify-between">
                    <span className="font-mono text-[10px] uppercase tracking-widest text-[#8696A0]">Saved replies</span>
                    <button onClick={() => setShowReplies(false)} className="text-[#8696A0] hover:text-[#111B21]"><X className="h-3.5 w-3.5" /></button>
                  </div>
                  {savedReplies.length === 0 && <p className="px-4 py-4 text-xs text-[#8696A0]">No saved replies yet. Add them in the Saved Replies page.</p>}
                  {savedReplies.map((r) => (
                    <button key={r.reply_id}
                      className="w-full text-left px-4 py-2.5 hover:bg-[#F0F2F5] border-b border-[#F0F2F5] transition-colors" onClick={() => insertReply(r)} data-testid={`use-saved-reply-${r.reply_id}`}>
                      <span className="text-sm text-[#111B21] font-medium">{r.title}</span>
                      <p className="text-xs text-[#8696A0] truncate">{r.content}</p>
                    </button>
                  ))}
                </div>
              )}
              {showTemplates && (
                <div className="absolute bottom-16 left-4 right-4 max-h-72 overflow-y-auto border border-[#E9EDEF] bg-white rounded-xl shadow-2xl z-30" data-testid="template-popover">
                  <div className="px-4 py-2.5 border-b border-[#E9EDEF] flex items-center justify-between">
                    <span className="font-mono text-[10px] uppercase tracking-widest text-[#8696A0]">Approved templates</span>
                    <button onClick={() => setShowTemplates(false)} className="text-[#8696A0] hover:text-[#111B21]"><X className="h-3.5 w-3.5" /></button>
                  </div>
                  {templates.length === 0 && <p className="px-4 py-4 text-xs text-[#8696A0]">No templates yet. Create them in Settings → Templates.</p>}
                  {templates.map((t) => (
                    <button key={t.template_id} data-testid={`use-template-${t.template_id}`} onClick={() => sendTemplate(t)}
                      className="w-full text-left px-4 py-2.5 hover:bg-[#F0F2F5] border-b border-[#F0F2F5] transition-colors">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-[#111B21] font-mono">{t.name}</span>
                        {t.status && <span className={`font-mono text-[9px] px-1.5 py-0.5 rounded-md border ${t.status === "APPROVED" ? "bg-[#DCF8C6] text-[#075E54] border-[#25D366]/40" : t.status === "REJECTED" ? "bg-red-100 text-red-700 border-red-200" : "bg-amber-100 text-amber-700 border-amber-200"}`}>{t.status}</span>}
                      </div>
                      <p className="text-xs text-[#8696A0] truncate">{t.body}</p>
                    </button>
                  ))}
                </div>
              )}
              <button data-testid="template-picker-button" onClick={() => setShowTemplates((s) => !s)}
                className="flex items-center justify-center h-11 w-11 flex-shrink-0 border border-[#CBD5E1] bg-white text-[#54656F] hover:bg-[#E9EDEF] rounded-full transition-colors" title="Approved templates">
                <Files className="h-4 w-4" />
              </button>
              <button data-testid="saved-replies-button" onClick={() => { setShowReplies((s) => !s); setShowTemplates(false); }}
                className="flex items-center justify-center h-11 w-11 flex-shrink-0 border border-[#CBD5E1] bg-white text-[#54656F] hover:bg-[#E9EDEF] rounded-full transition-colors" title="Saved replies">
                <MessageSquareText className="h-4 w-4" />
              </button>
              <input ref={mediaRef} type="file" onChange={sendMedia} className="hidden" data-testid="composer-media-input" />
              <button data-testid="composer-attach" onClick={() => mediaRef.current?.click()}
                className="flex items-center justify-center h-11 w-11 flex-shrink-0 border border-[#CBD5E1] bg-white text-[#54656F] hover:bg-[#E9EDEF] rounded-full transition-colors" title="Send image / document">
                <Paperclip className="h-4 w-4" />
              </button>
              <textarea ref={composerRef} data-testid="composer-input" value={input} rows={1}
                onChange={(e) => { setInput(e.target.value); e.target.style.height = "auto"; e.target.style.height = Math.min(e.target.scrollHeight, 140) + "px"; }}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); send(); } }}
                placeholder="Type a message"
                className="flex-1 min-w-0 resize-none leading-6 min-h-[44px] max-h-36 overflow-y-auto bg-white border border-[#CBD5E1] focus:border-[#25D366] focus:ring-2 focus:ring-[#25D366]/20 text-[#111B21] rounded-3xl px-4 py-2.5 text-base md:text-sm placeholder:text-[#8696A0] focus:outline-none transition-all" />
              <button data-testid="composer-send" onClick={send}
                className="flex items-center justify-center gap-2 h-11 flex-shrink-0 bg-[#25D366] text-[#111B21] hover:bg-[#20BD5A] rounded-full px-4 md:px-5 text-sm font-semibold shadow-sm transition-colors active:scale-[0.98]">
                <Send className="h-4 w-4" /> <span className="hidden sm:inline">Send</span>
              </button>
            </div>
          </>
        )}
      </div>

      {/* Right: customer info (static column on desktop, slide-over drawer on mobile/tablet) */}
      {conv && (
        <>
          {showInfo && <div className="fixed inset-0 z-40 bg-black/40 lg:hidden" onClick={() => setShowInfo(false)} data-testid="customer-info-backdrop" />}
          <div className={`bg-white border-l border-[#E9EDEF] overflow-y-auto min-h-0 fixed inset-y-0 right-0 z-50 w-[88%] max-w-sm shadow-2xl transform transition-transform duration-300 ${showInfo ? "translate-x-0" : "translate-x-full"} lg:static lg:z-auto lg:translate-x-0 lg:w-80 lg:max-w-none lg:flex-shrink-0 lg:shadow-none`} data-testid="customer-info-panel">
            <div className="h-16 flex items-center justify-between px-5 border-b border-[#E9EDEF] flex-shrink-0">
              <h2 className="font-heading text-sm font-bold text-[#111B21]">Customer</h2>
              <button className="lg:hidden text-[#8696A0] hover:text-[#111B21]" onClick={() => setShowInfo(false)} data-testid="customer-info-close"><X className="h-5 w-5" /></button>
            </div>
          <div className="p-5 space-y-5">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-full bg-[#25D366] flex items-center justify-center text-lg font-semibold text-white">{contact?.name?.[0]?.toUpperCase()}</div>
              <div>
                <p className="text-sm font-semibold text-[#111B21]">{contact?.name}</p>
                <p className="font-mono text-[10px] text-[#8696A0] flex items-center gap-1"><Phone className="h-3 w-3" />{contact?.phone}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 text-xs">
              <div className="border border-[#E9EDEF] rounded-xl p-3 bg-[#F8FAFC]">
                <p className="font-mono text-[9px] uppercase tracking-wider text-[#8696A0]">Lead Score</p>
                <p className="font-heading text-2xl font-bold text-[#075E54] mt-1">{lead?.score ?? "—"}</p>
              </div>
              <div className="border border-[#E9EDEF] rounded-xl p-3 bg-[#F8FAFC]">
                <p className="font-mono text-[9px] uppercase tracking-wider text-[#8696A0]">Rank</p>
                <p className={`text-sm font-semibold mt-1 ${["Hot", "Very Hot"].includes(lead?.category) ? "text-red-600" : lead?.category === "Warm" ? "text-yellow-600" : "text-[#54656F]"}`}>
                  {lead?.category ?? "—"} {lead?.temperature_source === "manual" && <span className="text-[9px] text-[#8696A0]">(manual)</span>}
                </p>
              </div>
            </div>

            {/* Manual rank assignment */}
            <div className="border border-[#E9EDEF] rounded-xl p-3 bg-white" data-testid="lead-rank-control">
              <p className="font-mono text-[9px] uppercase tracking-wider text-[#8696A0] mb-2">Assign Lead Rank</p>
              <div className="grid grid-cols-3 gap-1.5">
                {[["Cold", "border-[#E9EDEF] text-[#54656F]", "bg-[#F0F2F5] border-transparent text-[#54656F]"],
                  ["Warm", "border-yellow-200 text-yellow-600", "bg-yellow-500/10 border-yellow-400 text-yellow-700"],
                  ["Hot", "border-red-200 text-red-600", "bg-red-500/10 border-red-400 text-red-700"]].map(([r, idle, active]) => {
                  const on = lead?.category === r || (r === "Hot" && lead?.category === "Very Hot");
                  return (
                    <button key={r} data-testid={`set-rank-${r.toLowerCase()}`} onClick={() => setRank(r)}
                      className={`text-xs font-semibold rounded-lg border px-2 py-1.5 transition-colors ${on ? active : idle + " hover:bg-[#F8FAFC]"}`}>
                      {r === "Hot" ? "🔥 Hot" : r}
                    </button>
                  );
                })}
              </div>
              <button data-testid="set-rank-auto" onClick={() => setRank(null, "auto")}
                className="w-full mt-2 text-[11px] text-[#075E54] hover:text-[#128C7E] flex items-center justify-center gap-1">
                <Sparkles className="h-3 w-3" /> Reset to Auto (let AI decide)
              </button>
            </div>

            {/* Lead Status (outcome pipeline) */}
            {lead?.lead_id && (
              <div className="border border-[#E9EDEF] rounded-xl p-3 bg-white" data-testid="lead-status-control">
                <p className="font-mono text-[9px] uppercase tracking-wider text-[#8696A0] mb-2">Lead Status</p>
                <StatusSelect lead={lead}
                  onUpdated={(l) => { setDetail((d) => (d ? { ...d, lead: { ...(d.lead || {}), ...l } } : d)); setStatusRefresh((n) => n + 1); }} />
                {(lead?.quoted_amount || lead?.paid_amount) ? (
                  <div className="flex gap-3 mt-2 text-[11px] text-[#54656F]">
                    {lead?.quoted_amount ? <span>Quoted: ₹{lead.quoted_amount}</span> : null}
                    {lead?.paid_amount ? <span>Paid: ₹{lead.paid_amount}</span> : null}
                  </div>
                ) : null}
                {lead?.suggest_lost ? (
                  <p className="mt-2 text-[10px] text-red-600 bg-red-50 border border-red-200 rounded-md px-2 py-1">
                    Suggest: lost (no_response) — quoted 14+ days ago, no reply.
                  </p>
                ) : null}
                <div className="mt-3 pt-3 border-t border-[#F0F2F5]">
                  <p className="font-mono text-[9px] uppercase tracking-wider text-[#8696A0] mb-2">Status Timeline</p>
                  <StatusHistory leadId={lead.lead_id} refreshKey={statusRefresh} />
                </div>
              </div>
            )}

            {/* Follow-up Label (independent of temperature & enrollment) */}
            <div className="border border-[#E9EDEF] rounded-xl p-3 bg-white" data-testid="follow-up-label-control">
              <p className="font-mono text-[9px] uppercase tracking-wider text-[#8696A0] mb-2">Follow-up Label</p>
              <div className="grid grid-cols-3 gap-1.5">
                {FOLLOWUP_LABELS.map((it) => {
                  const on = detail?.lead?.follow_up_label === it.key;
                  return (
                    <button key={it.key} data-testid={`follow-label-${it.stage}`} onClick={() => chooseFollowLabel(it)}
                      className={`text-[11px] font-semibold rounded-lg border px-1.5 py-1.5 transition-colors flex items-center justify-center gap-1 ${on ? it.on : it.cls + " hover:brightness-95"}`}>
                      <it.icon className="h-3 w-3" /> {it.stage.charAt(0).toUpperCase() + it.stage.slice(1)}
                    </button>
                  );
                })}
              </div>
              <p className="text-[10px] text-[#8696A0] mt-2">A label is just a tag. It does not enrol the lead unless you confirm.</p>
            </div>

            {/* Automation status — one badge per active enrollment (a lead may be in multiple) */}
            {(autoInfo?.active_enrollments || []).length > 0 && (
              <div className="space-y-1.5" data-testid="automation-enrollments">
                <p className="font-mono text-[9px] uppercase tracking-wider text-[#8696A0]">Active Automations</p>
                {autoInfo.active_enrollments.map((e) => (
                  <button key={e.enrollment_id} onClick={() => { setPopupEnr(e); setShowAutoPopup(true); }}
                    data-testid={`automation-badge-${e.stage}`}
                    className="w-full border border-[#128C7E]/30 rounded-xl p-3 bg-[#E7FCE8] hover:bg-[#DCF8C6] text-left transition-colors">
                    <div className="flex items-center gap-2">
                      <Zap className="h-4 w-4 text-[#075E54] shrink-0" />
                      <span className="text-xs font-semibold text-[#075E54] truncate">{e.automation_name}</span>
                      <span className={`ml-auto text-[10px] px-2 py-0.5 rounded-md shrink-0 ${e.status === "active" ? "bg-[#25D366] text-white" : "bg-amber-400 text-white"}`}>
                        {e.status === "active" ? "Active" : "Paused"}
                      </span>
                    </div>
                    <p className="text-[11px] text-[#075E54]/70 mt-1">
                      Step {e.current_step}/{e.total_steps} · Next: {e.next_run_at ? new Date(e.next_run_at).toLocaleString(undefined, { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"}
                    </p>
                  </button>
                ))}
              </div>
            )}

            <div className="space-y-2 text-xs">
              <div className="flex justify-between"><span className="text-[#8696A0]">Source</span><span className="text-[#374151]">{contact?.source}</span></div>
              <div className="flex justify-between"><span className="text-[#8696A0]">First contacted</span><span className="text-[#374151] font-mono">{contact?.created_at ? new Date(contact.created_at).toLocaleDateString() : "—"}</span></div>
              <div className="flex justify-between"><span className="text-[#8696A0]">Last contacted</span><span className="text-[#374151] font-mono">{conv?.last_message_at ? new Date(conv.last_message_at).toLocaleDateString() : "—"}</span></div>
            </div>

            <div className="border-t border-[#E9EDEF] pt-4">
              <button data-testid="generate-summary" onClick={genSummary} disabled={summaryLoading}
                className="w-full flex items-center justify-center gap-2 bg-[#E7FCE8] text-[#075E54] border border-[#25D366]/30 hover:bg-[#DCF8C6] rounded-xl px-3 py-2.5 text-xs font-semibold transition-colors disabled:opacity-50">
                <Sparkles className="h-3.5 w-3.5" /> {summaryLoading ? "Generating…" : "AI Conversation Summary"}
              </button>
              {summary && (
                <div className="mt-3 space-y-2 text-xs border border-[#E9EDEF] rounded-xl p-3 bg-[#F8FAFC]" data-testid="summary-panel">
                  <div className="flex items-center justify-between">
                    <p className="font-mono text-[9px] uppercase tracking-widest text-[#8696A0]">Summary</p>
                    <button data-testid="copy-summary"
                      onClick={() => safeCopy([["Requirement", summary.requirement], ["Budget", summary.budget], ["Timeline", summary.timeline], ["Intent", summary.intent], ["Chat Language", summary.chat_language], ["Recommended", summary.recommended_action]].map(([k, v]) => `${k}: ${v || "—"}`).join("\n"))}
                      className="flex items-center gap-1 text-[10px] font-semibold text-[#075E54] hover:text-[#128C7E] bg-[#E7FCE8] border border-[#25D366]/30 rounded-lg px-2 py-1 transition-colors">
                      <Copy className="h-3 w-3" /> Copy
                    </button>
                  </div>
                  {[["Requirement", summary.requirement], ["Budget", summary.budget], ["Timeline", summary.timeline], ["Intent", summary.intent], ["Chat Language", summary.chat_language], ["Recommended", summary.recommended_action]].map(([k, v]) => (
                    <div key={k}>
                      <p className="font-mono text-[9px] uppercase tracking-wider text-[#8696A0]">{k}</p>
                      <p className="text-[#374151]">{v}</p>
                    </div>
                  ))}
                </div>
              )}
              <div className="mt-4 border-t border-[#E9EDEF] pt-4" data-testid="reply-translator">
                <p className="font-mono text-[9px] uppercase tracking-wider text-[#8696A0] mb-2 flex items-center gap-1.5"><Languages className="h-3 w-3" /> Reply Translator</p>
                <div className="flex items-center gap-1.5 mb-2">
                  <span className="text-[10px] text-[#8696A0] whitespace-nowrap">To language</span>
                  <input data-testid="reply-lang-input" value={replyLang} onChange={(e) => setReplyLang(e.target.value)} placeholder="e.g. Hindi"
                    className="flex-1 min-w-0 text-xs px-2 py-1 border border-[#CBD5E1] rounded-lg focus:outline-none focus:border-[#25D366] text-[#111B21]" />
                </div>
                <textarea data-testid="reply-translator-input" value={replyEn} onChange={(e) => setReplyEn(e.target.value)} rows={2}
                  placeholder="Type your reply in English…"
                  className="w-full text-sm px-2 py-1.5 border border-[#CBD5E1] rounded-lg focus:outline-none focus:border-[#25D366] text-[#111B21] resize-none" />
                <button data-testid="reply-translate-btn" onClick={doReplyTranslate} disabled={replyBusy || !replyEn.trim()}
                  className="w-full mt-2 flex items-center justify-center gap-2 bg-[#075E54] text-white hover:bg-[#0a7267] rounded-xl px-3 py-2 text-xs font-semibold transition-colors disabled:opacity-50">
                  {replyBusy ? "Translating…" : `Translate${replyLang ? " to " + replyLang : ""}`}
                </button>
                {replyOut && (
                  <div className="mt-2 border border-[#E9EDEF] rounded-xl p-2.5 bg-[#F8FAFC]" data-testid="reply-translator-output">
                    <p className="text-sm whitespace-pre-wrap break-words text-[#111B21]">{replyOut}</p>
                    <div className="flex gap-1.5 mt-2">
                      <button data-testid="reply-copy-btn" onClick={() => safeCopy(replyOut)}
                        className="flex-1 flex items-center justify-center gap-1.5 text-xs px-2 py-1.5 rounded-lg bg-[#E7FCE8] text-[#075E54] border border-[#25D366]/30 hover:bg-[#DCF8C6] font-semibold"><Copy className="h-3 w-3" /> Copy</button>
                      <button data-testid="reply-insert-btn" onClick={() => { setInput((p) => (p ? p + "\n" : "") + replyOut); toast.success("Added to reply box"); }}
                        className="flex-1 text-xs px-2 py-1.5 rounded-lg bg-[#25D366] text-[#111B21] font-semibold">Insert</button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          </div>
        </>
      )}
      {lightbox && <ImageLightbox path={lightbox} onClose={() => setLightbox(null)} />}

      {/* Enroll confirmation after adding a follow-up label */}
      {confirmStage && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4" onClick={() => setConfirmStage(null)} data-testid="enroll-confirm-modal">
          <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-2">
              <Zap className="h-5 w-5 text-[#075E54]" />
              <h3 className="font-heading text-sm font-semibold text-[#111B21]">Add to automation?</h3>
            </div>
            <p className="text-sm text-[#54656F]">Add this lead to the <b>{confirmStage.stage.charAt(0).toUpperCase() + confirmStage.stage.slice(1)} follow-up</b> automation? The label has been saved either way.</p>
            <div className="flex gap-2 mt-4">
              <button data-testid="enroll-confirm-yes" onClick={() => confirmEnroll(true)}
                className="flex-1 bg-[#075E54] text-white hover:bg-[#128C7E] rounded-xl px-4 py-2.5 text-sm font-semibold">Add to Automation</button>
              <button data-testid="enroll-confirm-no" onClick={() => confirmEnroll(false)}
                className="flex-1 border border-[#E9EDEF] text-[#54656F] hover:bg-[#F0F2F5] rounded-xl px-4 py-2.5 text-sm font-semibold">Not Now</button>
            </div>
          </div>
        </div>
      )}

      {/* Automation status popup (scoped to the clicked enrollment) */}
      {showAutoPopup && popupEnr && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4" onClick={() => setShowAutoPopup(false)} data-testid="automation-popup">
          <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#E9EDEF]">
              <h3 className="font-heading text-sm font-semibold text-[#111B21] flex items-center gap-2"><Zap className="h-4 w-4 text-[#075E54]" /> {popupEnr.automation_name}</h3>
              <button onClick={() => setShowAutoPopup(false)} className="text-[#8696A0] hover:text-[#111B21]"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-5 space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-[#8696A0]">Status</span><span className="font-semibold capitalize text-[#111B21]">{popupEnr.status}</span></div>
              <div className="flex justify-between"><span className="text-[#8696A0]">Current step</span><span className="font-semibold text-[#111B21]">{popupEnr.current_step}/{popupEnr.total_steps}</span></div>
              <div className="flex justify-between"><span className="text-[#8696A0]">Next follow-up</span><span className="font-semibold text-[#111B21]">{popupEnr.next_run_at ? new Date(popupEnr.next_run_at).toLocaleString(undefined, { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"}</span></div>
              {popupEnr.stop_reason && <p className="text-xs text-red-500">{popupEnr.stop_reason}</p>}
              <div className="flex flex-wrap gap-2 pt-3">
                {popupEnr.status === "active"
                  ? <button data-testid="auto-pause" onClick={() => autoAction("pause", popupEnr.enrollment_id)} className="flex-1 flex items-center justify-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-xl border border-[#E9EDEF] hover:bg-[#F0F2F5]"><PauseCircle className="h-4 w-4" /> Pause</button>
                  : <button data-testid="auto-resume" onClick={() => autoAction("resume", popupEnr.enrollment_id)} className="flex-1 flex items-center justify-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-xl border border-[#E9EDEF] hover:bg-[#F0F2F5] text-[#075E54]"><PlayCircle className="h-4 w-4" /> Resume</button>}
                <button data-testid="auto-remove" onClick={() => autoAction("remove", popupEnr.enrollment_id)} className="flex-1 flex items-center justify-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-xl border border-[#E9EDEF] hover:bg-red-50 text-red-500"><Ban className="h-4 w-4" /> Remove</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Case B — already enrolled in the same automation */}
      {alreadyInfo && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4" onClick={() => setAlreadyInfo(null)} data-testid="already-enrolled-modal">
          <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-2"><Zap className="h-5 w-5 text-[#075E54]" /><h3 className="font-heading text-sm font-semibold text-[#111B21]">Already enrolled</h3></div>
            <p className="text-sm text-[#54656F]">This lead is already in <b>{alreadyInfo.name}</b>.</p>
            <div className="flex gap-2 mt-4">
              <button data-testid="already-ok" onClick={() => setAlreadyInfo(null)} className="flex-1 border border-[#E9EDEF] text-[#54656F] hover:bg-[#F0F2F5] rounded-xl px-4 py-2.5 text-sm font-semibold">OK</button>
              <button data-testid="already-view" onClick={() => { const e = (autoInfo?.active_enrollments || []).find((x) => x.automation_name === alreadyInfo.name); setAlreadyInfo(null); if (e) { setPopupEnr(e); setShowAutoPopup(true); } }} className="flex-1 bg-[#075E54] text-white hover:bg-[#128C7E] rounded-xl px-4 py-2.5 text-sm font-semibold">View Automation</button>
            </div>
          </div>
        </div>
      )}

      {/* Case C — already in a different automation; confirm second enrollment */}
      {conflictEnroll && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4" onClick={() => setConflictEnroll(null)} data-testid="conflict-enroll-modal">
          <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-2"><Zap className="h-5 w-5 text-amber-500" /><h3 className="font-heading text-sm font-semibold text-[#111B21]">Already in another automation</h3></div>
            <p className="text-sm text-[#54656F]">This lead is already in <b>{conflictEnroll.existing.join(", ")}</b>.</p>
            <p className="text-sm text-[#54656F] mt-2">Do you still want to add this lead to <b>{conflictEnroll.requestedName}</b>?</p>
            <div className="flex gap-2 mt-4">
              <button data-testid="conflict-cancel" onClick={() => setConflictEnroll(null)} className="flex-1 border border-[#E9EDEF] text-[#54656F] hover:bg-[#F0F2F5] rounded-xl px-4 py-2.5 text-sm font-semibold">Cancel</button>
              <button data-testid="conflict-add" onClick={() => { const s = conflictEnroll.stage; setConflictEnroll(null); doEnroll(s, true); }} className="flex-1 bg-[#075E54] text-white hover:bg-[#128C7E] rounded-xl px-4 py-2.5 text-sm font-semibold">Add to {conflictEnroll.requestedName}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
